import { RefreshCw } from 'lucide-react';
import { lazy, Suspense } from 'react';

import { useI18n } from '../../i18n/useI18n';
import { AddMovementDialog } from './AddMovementDialog';
import { AgendaView } from './AgendaView';
import { CalendarView } from './CalendarView';
import { HomeView } from './HomeView';
import { PlanView } from './PlanView';
import type { DashboardView } from './dashboard.types';
import { useDashboardData } from './useDashboardData';
import { PlanStatusBanner } from '../billing/PlanStatusBanner';
import { ApiError } from '../../lib/api-client';
import { FirstProjectionGuide } from '../onboarding/FirstProjectionGuide';

const AdviserPanel = lazy(async () => ({
  default: (await import('../adviser/AdviserPanel')).AdviserPanel,
}));
const QuickExpenseView = lazy(async () => ({
  default: (await import('./QuickExpenseView')).QuickExpenseView,
}));
const ToolsView = lazy(async () => ({
  default: (await import('./ToolsView')).ToolsView,
}));

interface DashboardProps {
  readonly addMovementOpen: boolean;
  readonly filtersOpen: boolean;
  readonly onCloseAddMovement: () => void;
  readonly onCloseFilters: () => void;
  readonly onOpenAddMovement: () => void;
  readonly onOpenFilters: () => void;
  readonly onOpenTools: () => void;
  readonly onCloseToolsFilters: () => void;
  readonly onTransactionCreated: (transactionId: string) => void;
  readonly onUpgrade: () => void;
  readonly lastAddedTransactionId: string | undefined;
  readonly toolsFiltersOpen: boolean;
  readonly view: DashboardView;
}

export function Dashboard({
  addMovementOpen,
  filtersOpen,
  lastAddedTransactionId,
  onCloseAddMovement,
  onCloseFilters,
  onOpenAddMovement,
  onOpenFilters,
  onOpenTools,
  onCloseToolsFilters,
  onTransactionCreated,
  onUpgrade,
  toolsFiltersOpen,
  view,
}: DashboardProps) {
  const { t } = useI18n();
  const { error, isFetching, isPending, refresh, snapshot } = useDashboardData();

  if (snapshot === null) {
    const trialExpired = error instanceof ApiError && error.code === 'TRIAL_EXPIRED';
    return (
      <main className="app-content" id="main-content" tabIndex={-1}>
        <DashboardHeading view={view} />
        {isPending && error === undefined ? (
          <DashboardSkeleton />
        ) : trialExpired ? (
          <TrialExpiredState onUpgrade={onUpgrade} />
        ) : (
          <section className="error-state" role="alert">
            <h2>{t('dashboard.loadError')}</h2>
            <p>{t('dashboard.loadErrorDescription')}</p>
            <button className="button button-primary" type="button" onClick={() => void refresh()}>
              <RefreshCw aria-hidden="true" /> {t('common.retry')}
            </button>
          </section>
        )}
      </main>
    );
  }

  return (
    <main className="app-content" id="main-content" tabIndex={-1}>
      <PlanStatusBanner onUpgrade={onUpgrade} />
      {error !== undefined && (
        <div className="offline-banner" role="status">
          {t('dashboard.staleData')}
        </div>
      )}
      {isFetching && (
        <span className="sync-status" role="status">
          {t('dashboard.statusUpdating')}
        </span>
      )}
      {view === 'home' && (
        <>
          <FirstProjectionGuide onTransactionCreated={onTransactionCreated} snapshot={snapshot} />
          <HomeView snapshot={snapshot} />
          <Suspense fallback={<AdviserLoadingFallback />}>
            <AdviserPanel
              compact
              lastAddedTransactionId={lastAddedTransactionId}
              snapshot={snapshot}
            />
          </Suspense>
        </>
      )}
      {view === 'plan' && (
        <PlanView
          filtersOpen={filtersOpen}
          onAddMovement={onOpenAddMovement}
          onCloseFilters={onCloseFilters}
          onOpenFilters={onOpenFilters}
          onOpenTools={onOpenTools}
          snapshot={snapshot}
        />
      )}
      {view === 'agenda' && <AgendaView snapshot={snapshot} />}
      {view === 'calendar' && <CalendarView snapshot={snapshot} />}
      {view === 'quick' && (
        <Suspense fallback={<DashboardSkeleton />}>
          <QuickExpenseView onCreated={onTransactionCreated} snapshot={snapshot} />
        </Suspense>
      )}
      {view === 'tools' && (
        <Suspense fallback={<DashboardSkeleton />}>
          <ToolsView
            filtersOpen={toolsFiltersOpen}
            onCloseFilters={onCloseToolsFilters}
            snapshot={snapshot}
          />
        </Suspense>
      )}
      {addMovementOpen && (
        <AddMovementDialog
          categories={snapshot.categories}
          onClose={onCloseAddMovement}
          onCreated={onTransactionCreated}
          today={snapshot.today}
          wallets={snapshot.wallets}
        />
      )}
    </main>
  );
}

function TrialExpiredState({ onUpgrade }: { readonly onUpgrade: () => void }) {
  const { t } = useI18n();
  return (
    <section className="error-state trial-expired-state" role="alert">
      <h2>{t('dashboard.trialExpired')}</h2>
      <p>{t('dashboard.trialExpiredDescription')}</p>
      <button className="button button-primary" type="button" onClick={onUpgrade}>
        {t('billing.viewPlans')}
      </button>
    </section>
  );
}

function DashboardHeading({ view }: { readonly view: DashboardView }) {
  const { t } = useI18n();
  const titles = {
    agenda: t('dashboard.agendaTitle'),
    calendar: t('dashboard.calendarTitle'),
    home: t('dashboard.viewTitle'),
    plan: t('dashboard.monthTitle'),
    quick: t('dashboard.quickTitle'),
    tools: t('dashboard.toolsTitle'),
  } as const;
  return (
    <header className="dashboard-heading">
      <p className="eyebrow">FinanceGo</p>
      <h1>{titles[view]}</h1>
      <p>{t('dashboard.loadingFinancial')}</p>
    </header>
  );
}

function DashboardSkeleton() {
  const { t } = useI18n();
  return (
    <div aria-busy="true" aria-label={t('common.loadingDashboard')} className="dashboard-skeleton">
      <span />
      <span />
      <span />
    </div>
  );
}

function AdviserLoadingFallback() {
  const { t } = useI18n();
  return (
    <section
      aria-busy="true"
      aria-label={t('common.loadingAdviser')}
      className="adviser-panel adviser-loading"
    >
      <span />
      <span />
      <span />
    </section>
  );
}
